import { Modal, ModalDialog, Stack } from '@mui/joy';
import { type FunctionComponent, useMemo, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  createAccountAutocompleteOptions,
  createSubAccountAutocompleteOptions,
  type CreateSubAccountIdAutocompleteOptionsInputAccount,
} from 'components/portfolio/account/AccountService.tsx';
import GFormProvider from 'components/technical/form/GFormProvider';
import { GraphQLApiFormErrorMessage } from 'components/technical/form/GraphQLApiErrorMessage';
import gYupResolver from 'components/technical/form/gYupResolver';
import SubmitButton from 'components/technical/form/SubmitButton';
import { type GraphQlErrorHandler, useGraphQLApiError } from 'components/technical/form/UseGraphQLApiError';
import GDialogHeader from 'components/technical/GDialog/GDialogHeader.tsx';
import GButton from 'components/technical/inputs/GButton/GButton';
import { defaultRowSpacing } from 'components/StackSpacing';
import type { SubFund, SubFundCorrelationAssets } from './SubFundsDashboard';
import { Edit } from '@mui/icons-material';
import { sortBy } from 'lodash/fp';
import { createAssetSelectOptions } from 'components/market/asset/AssetService';
import FormSelectedChipsBoard from 'components/technical/form/FormSelectedChipsBoard';
import { IconVariant } from 'components/market/asset/cryptocurrencies/CryptocurrenciesData';
import { AccountLabel } from '../account/AccountLabel';
import { FormInput } from 'components/technical/inputs';
import FormStaticSingleAutocomplete from 'components/technical/form/FormStaticSingleAutocomplete';
import StaticMultiAutocomplete from 'components/technical/inputs/Autocomplete/StaticMultiAutocomplete';
import { SubAccountLabel } from 'components/portfolio/account/SubAccountLabel';

type SubFundFormState = {
  name: string;
  accounts: SubFund['accounts'];
  subAccounts: SubFund['subAccounts'];
  referenceAsset: SubFund['referenceAsset'] | null;
};

type CreateUpdateSubFundDialogProps = {
  onClose: () => void;
  accounts: CreateSubAccountIdAutocompleteOptionsInputAccount[];
  existingSubFunds: Set<string>;
  existingSubFundsAccounts?: Set<string> | undefined;
  existingSubFundsSubAccounts?: Set<string> | undefined;
  handleFormSubmit: (data: SubFundFormState, graphQlErrorHandler: GraphQlErrorHandler) => Promise<void>;
  initialState: SubFund;
  correlationAssets: SubFundCorrelationAssets;
};

const ChangeSubFundAccountsDialog: FunctionComponent<CreateUpdateSubFundDialogProps> = (props) => {
  const checkUniqueSubFund = (value: string) => {
    // If the new name is the same as the current name ignore the check
    return props.initialState.name === value || !props.existingSubFunds.has(value);
  };

  const formSchema = yup
    .object({
      name: yup
        .string()
        .required()
        .test('unique', 'Sub-fund already exists', (value) => checkUniqueSubFund(value)),
      accounts: yup.array().of(yup.mixed().required()).required().default([]),
      subAccounts: yup.array().of(yup.mixed().required()).required().default([]),
      referenceAsset: yup.object().nullable(),
    })
    .test('at-least-one-selection', 'At least one account or sub-account is required', function (value) {
      const { accounts, subAccounts } = value;
      const hasAccounts = accounts && accounts.length > 0;
      const hasSubAccounts = subAccounts && subAccounts.length > 0;

      if (!hasAccounts && !hasSubAccounts) {
        return new yup.ValidationError([
          this.createError({
            path: 'accounts',
            message: 'At least one account or sub-account is required',
          }),
          this.createError({
            path: 'subAccounts',
            message: 'At least one account or sub-account is required',
          }),
        ]);
      }

      return true;
    });

  const initialReferenceAsset = props.correlationAssets.find(
    (asset) => asset.id === props.initialState.referenceAsset?.id
  );
  const initialAccounts =
    props.initialState.accounts.length > 0 ? sortBy((a) => a.name, props.initialState.accounts) : [];
  const initialSubAccounts =
    props.initialState.subAccounts.length > 0 ? sortBy((a) => a.name, props.initialState.subAccounts) : [];

  const methods = useForm<SubFundFormState>({
    resolver: gYupResolver(formSchema),
    reValidateMode: 'onChange',
    defaultValues: {
      name: props.initialState.name,
      accounts: initialAccounts,
      subAccounts: initialSubAccounts,
      referenceAsset: initialReferenceAsset ?? null,
    },
  });

  const selectedAccounts = methods.watch('accounts');
  const selectedSubAccounts = methods.watch('subAccounts') || [];

  // Clear validation errors when either accounts or subAccounts change
  useEffect(() => {
    const hasAccounts = selectedAccounts && selectedAccounts.length > 0;
    const hasSubAccounts = selectedSubAccounts && selectedSubAccounts.length > 0;

    if (hasAccounts || hasSubAccounts) {
      methods.clearErrors(['accounts', 'subAccounts']);
    }
  }, [selectedAccounts, selectedSubAccounts, methods]);
  const availableAccounts = props.accounts.filter((account) => !selectedAccounts.map((a) => a.id).includes(account.id));

  const existingData = {
    accounts: new Set([...(props.existingSubFundsAccounts || []), ...selectedAccounts.map((account) => account.id)]),
    subAccounts: new Set([
      ...(props.existingSubFundsSubAccounts || []),
      ...selectedSubAccounts.map((subAccount) => subAccount.id),
    ]),
  };

  const availableToAddAccountOptions = createAccountAutocompleteOptions(availableAccounts, {
    ignoreData: existingData,
  });
  const availableToAddSubAccountOptions = createSubAccountAutocompleteOptions(availableAccounts, {
    ignoreData: existingData,
  });

  const assetOptions = useMemo(() => createAssetSelectOptions(props.correlationAssets), [props.correlationAssets]);

  const { onErrorAndThrow } = useGraphQLApiError(methods);
  const [autocompleteClearAccounts, setAutocompleteClearAccounts] = useState(0);
  const [autocompleteClearSubAccounts, setAutocompleteClearSubAccounts] = useState(0);

  return (
    <Modal open onClose={props.onClose}>
      <ModalDialog layout="center" size="lg">
        <GFormProvider {...methods}>
          <form onSubmit={methods.handleSubmit((data) => props.handleFormSubmit(data, onErrorAndThrow))}>
            <GDialogHeader>{methods.getValues('name')}</GDialogHeader>
            <Stack spacing={3} alignItems="start">
              <Stack direction="row" gap={10} maxWidth={'100%'}>
                <Stack spacing={3.5}>
                  <FormInput
                    type="input"
                    showLabelAboveInput
                    placeholder="Name"
                    label="Name"
                    name="name"
                    autoComplete="off"
                    width="xl2"
                  />
                  <FormStaticSingleAutocomplete
                    name="referenceAsset"
                    label="Reference Asset"
                    placeholder="Search"
                    width="fullWidth"
                    showLabelAboveInput
                    {...assetOptions}
                  />
                </Stack>
                <Stack direction="column" gap={3.5} maxWidth={'100%'}>
                  <Stack direction="column" gap={3.5} maxWidth={'100%'}>
                    <StaticMultiAutocomplete
                      value={[]}
                      name="accounts"
                      label="Accounts"
                      placeholder="Search"
                      width="fullWidth"
                      menuWidth="xl3"
                      showChipTooltips
                      key={autocompleteClearAccounts}
                      showLabelAboveInput
                      {...availableToAddAccountOptions}
                      onChange={(accountToAdd) => {
                        if (accountToAdd) {
                          methods.setValue('accounts', methods.getValues('accounts').concat(accountToAdd), {
                            shouldValidate: true,
                          });
                        }
                        setAutocompleteClearAccounts((prevAutocompleteClearKey) => prevAutocompleteClearKey + 1);
                      }}
                    />
                    <FormSelectedChipsBoard<SubFundFormState>
                      name="accounts"
                      label={`Selected Accounts for ${props.initialState.name}`}
                      placeholder="Accounts"
                      height={150}
                      width="xl4"
                      getItemKey={(account) => account.id}
                      renderItem={(account) => (
                        <AccountLabel account={account} size={IconVariant.MEDIUM} wrap={false} />
                      )}
                    />
                  </Stack>
                  <Stack direction="column" gap={3.5} maxWidth={'100%'}>
                    <StaticMultiAutocomplete
                      value={[]}
                      name="subAccounts"
                      label="Sub-account"
                      width="fullWidth"
                      placeholder="Search"
                      menuWidth="xl3"
                      showChipTooltips
                      key={autocompleteClearSubAccounts}
                      showLabelAboveInput
                      {...availableToAddSubAccountOptions}
                      onChange={(subAccountToAdd) => {
                        if (subAccountToAdd) {
                          methods.setValue('subAccounts', methods.getValues('subAccounts').concat(subAccountToAdd), {
                            shouldValidate: true,
                          });
                        }
                        setAutocompleteClearSubAccounts((prevAutocompleteClearKey) => prevAutocompleteClearKey + 1);
                      }}
                    />
                    <FormSelectedChipsBoard<SubFundFormState>
                      name="subAccounts"
                      label={`Selected Sub Accounts for ${props.initialState.name}`}
                      placeholder="Sub Accounts"
                      height={150}
                      width="xl4"
                      getItemKey={(subAccount) => subAccount.id}
                      renderItem={(subAccount) => (
                        <SubAccountLabel subAccount={subAccount} size={IconVariant.MEDIUM} wrap={false} />
                      )}
                    />
                  </Stack>
                </Stack>
              </Stack>
              <Stack width="100%">
                <GraphQLApiFormErrorMessage />
                <Stack direction="row" justifyContent="center" spacing={defaultRowSpacing}>
                  <GButton width="normal" variant="outlined" onClick={props.onClose}>
                    Cancel
                  </GButton>
                  <SubmitButton width="normal" startDecorator={<Edit />}>
                    Save
                  </SubmitButton>
                </Stack>
              </Stack>
            </Stack>
          </form>
        </GFormProvider>
      </ModalDialog>
    </Modal>
  );
};

export default ChangeSubFundAccountsDialog;
